import React, { useContext } from 'react';
import { useApolloClient } from '@apollo/react-hooks';
// context
import { useConfigSelectors } from 'src/app/reducers/configReducer';
import { useLanguageSelectors } from 'src/app/reducers/languageReducer';
import { InfringementDispatchContext } from 'src/@infringementsCandidates/providers/InfringementsStateProvider';
import { useSortSelectors } from 'src/@infringementsCandidates/reducers';
// common components
import { Filter, FilterRow, TimeRangeSelector, IDropdownItem } from '@ems/client-design-system';
import {
  updateSelectedItems,
  clearSelectedItems,
  updateTimeFilterValue,
  updateTimeFilterInput,
} from 'src/@infringementsCandidates/actions/filterActions';
// store
import { filterStore } from 'src/@infringementsCandidates/stores/filterStore';
// function
import {
  useLatestFilter,
  getIfFiltersEmpty,
  translateLabels,
  useTimeFilter,
} from 'src/@infringementsCandidates/functions';
import { capitalizeObjectKeys } from 'src/utils/objectModifiers';
// interfaces
import { ITableFilterItem } from 'src/@infringementsCandidates/interfaces';
// constants
import { NULL_VALUE } from 'src/constants';
import { useInfringementRulesSelectors } from 'src/app/reducers';

export const FilterContainer = ({
  candidateFlag,
  setCandidateFlag,
}: {
  candidateFlag: boolean;
  setCandidateFlag: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const client = useApolloClient();
  // Configuration
  const configSelectors = useConfigSelectors();
  const dispatcher = useContext(InfringementDispatchContext);
  const sortSelectors = useSortSelectors();
  const sortString = sortSelectors.getSortString();
  const {
    grid: { resultSize },
    infringementsCandidates: {
      filter: { availableCategoryList },
    },
  } = configSelectors.getConfig();
  const nullable: any = [];
  const showSelectedIcons: any = [];
  // Translation
  const languageSelectors = useLanguageSelectors();
  const {
    components: {
      labels: {
        filters: { clear: clearValue, filter: filterValue, clearFilters },
      },
      filters: {
        time: { buttonTitle, validationError },
      },
      hints: { noMatchesFound },
      lists: {
        infringementFilterCategories,
        aircraftCategories,
        candidates,
        ruleTypes,
        severities,
        extraFilterValues,
        infringementStatuses,
      },
    },
  } = languageSelectors.getLanguage();

  const timeFilters = useTimeFilter(resultSize);

  const dataListUpdated = capitalizeObjectKeys({
    ...aircraftCategories,
    ...candidates,
    ...extraFilterValues,
    ...ruleTypes,
    ...severities,
    ...infringementStatuses,
  });

  const infringementRulesSelector = useInfringementRulesSelectors();
  const infringementRulesList: IDropdownItem[] = infringementRulesSelector
    .getRules()
    .map(({ name }) => ({ key: name, label: name }));

  const filterItems = filterStore.getAllFilterOptions();
  const selectedItems = useLatestFilter({
    extraFilterValues,
  });
  const filterComponents: JSX.Element[] = [];
  availableCategoryList.map((category: string) => {
    const NullValue = { key: NULL_VALUE, label: NULL_VALUE, icon: undefined };
    const originalItems = filterItems[category];

    if (category === 'ruleNames') {
      const NullValue = { key: NULL_VALUE, label: NULL_VALUE, icon: undefined };
      const transformedItems =
        nullable && nullable.includes(category)
          ? [NullValue, ...infringementRulesList]
          : infringementRulesList;
      filterComponents.push(
        <Filter
          key={category}
          categoryName={infringementFilterCategories[category]}
          filterItems={translateLabels(transformedItems, dataListUpdated)}
          selectedItems={selectedItems[category]}
          updateItems={(items: ITableFilterItem[]) => {
            updateSelectedItems(
              client,
              category,
              items,
              dispatcher,
              sortString,
              resultSize,
              setCandidateFlag,
              candidateFlag
            );
          }}
          iconCategories={showSelectedIcons.map(
            (category: string) => infringementFilterCategories[category]
          )}
          languageData={{ clearValue, filterValue, noMatchesFound }}
        />
      );
    } else if (originalItems !== undefined) {
      const transformedItems =
        nullable && nullable.includes(category) ? [NullValue, ...originalItems] : originalItems;
      filterComponents.push(
        <Filter
          key={category}
          categoryName={infringementFilterCategories[category]}
          filterItems={translateLabels(transformedItems, dataListUpdated)}
          selectedItems={selectedItems[category]}
          updateItems={(items: ITableFilterItem[]) =>
            updateSelectedItems(
              client,
              category,
              items,
              dispatcher,
              sortString,
              resultSize,
              setCandidateFlag,
              candidateFlag
            )
          }
          iconCategories={showSelectedIcons.map(
            (category: string) => infringementFilterCategories[category]
          )}
          languageData={{ clearValue, filterValue, noMatchesFound }}
        />
      );
    }
  });

  /*
   * arr.splice(index, 0, item); will insert
   * item into arr at the specified index
   * (deleting 0 items first, that is, it's just an insert).
   */
  filterComponents.splice(
    1,
    0,
    <TimeRangeSelector
      key="filter-time-selector"
      fromInputValue={timeFilters.fromInput}
      toInputValue={timeFilters.toInput}
      setInputValue={updateTimeFilterInput}
      fromTime={timeFilters.from}
      toTime={timeFilters.to}
      setTime={updateTimeFilterValue}
      languageData={{ buttonTitle, validationError }}
    />
  );

  return (
    <div>
      <FilterRow
        filters={filterComponents}
        clearFilters={() => {
          clearSelectedItems(client, resultSize, dispatcher, sortString);
          setCandidateFlag(false);
        }}
        clearDisabled={getIfFiltersEmpty(selectedItems, timeFilters)}
        languageData={{ clearFilters }}
      />
    </div>
  );
};
